/**
 * Agent Orchestration View
 * Visualizes the agentic task execution and dependencies in real-time
 */

import React, { useState } from 'react';
import {
  Bot,
  CheckCircle2,
  XCircle,
  Clock,
  Activity,
  AlertCircle,
  ChevronRight,
  Zap,
  Database,
  GitBranch,
  RefreshCw,
  Play
} from 'lucide-react';
import { IntegrationExecution, AgentTask, TaskStatus } from '@/services/api/integrations';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface Props {
  execution: IntegrationExecution;
  isRunning: boolean;
}

export const AgentOrchestrationView: React.FC<Props> = ({ execution, isRunning }) => {
  const { t } = useAppTranslation();
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());

  if (!execution.execution_plan) {
    return (
      <div className="p-8 text-center">
        <Bot className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">{t('integrations.no_execution_plan_available', 'No execution plan available')}</p>
        <p className="text-sm text-gray-400 mt-2">
          {t('integrations.execution_plan_will_be_generated', 'Execution plan will be generated when the integration runs')}
        </p>
      </div>
    );
  }

  const plan = execution.execution_plan;
  const tasks = plan.tasks || [];

  // Calculate statistics
  const stats = {
    total: tasks.length,
    pending: tasks.filter(task => task.status === 'pending').length,
    running: tasks.filter(task => task.status === 'running').length,
    completed: tasks.filter(task => task.status === 'completed').length,
    failed: tasks.filter(task => task.status === 'failed').length,
    retry: tasks.filter(task => task.status === 'retry').length,
  };

  const progress = stats.total > 0 ? ((stats.completed + stats.failed) / stats.total) * 100 : 0;

  const toggleTask = (taskId: string) => {
    const newExpanded = new Set(expandedTasks);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedTasks(newExpanded);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Zap className="h-5 w-5 text-teal-600" />
              {t('integrations.agent_orchestration', 'Agent Orchestration')}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Plan ID: {plan.plan_id}
            </p>
          </div>

          {isRunning && (
            <div className="flex items-center gap-2 text-blue-600">
              <Activity className="h-5 w-5 animate-pulse" />
              <span className="font-medium">{t('integrations.running', 'Running...')}</span>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              stats.failed > 0 ? 'bg-red-500' : 'bg-teal-600'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-600 mt-1">
          <span>{Math.round(progress)}% Complete</span>
          <span>{stats.completed + stats.failed} / {stats.total} tasks</span>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-6 gap-3 mb-6">
        <TaskStat label={t('integrations.total', 'Total')} count={stats.total} color="gray" />
        <TaskStat label={t('integrations.pending', 'Pending')} count={stats.pending} color="gray" icon={<Clock />} />
        <TaskStat label={t('integrations.running_1', 'Running')} count={stats.running} color="blue" icon={<Activity />} />
        <TaskStat label={t('integrations.completed', 'Completed')} count={stats.completed} color="green" icon={<CheckCircle2 />} />
        <TaskStat label={t('integrations.failed', 'Failed')} count={stats.failed} color="red" icon={<XCircle />} />
        <TaskStat label={t('integrations.retry', 'Retry')} count={stats.retry} color="yellow" icon={<RefreshCw />} />
      </div>

      {/* Task Flow Visualization */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <GitBranch className="h-4 w-4" />
          {t('integrations.task_flow', 'Task Flow')}
        </h4>
        <div className="space-y-2">
          {tasks.map((task, index) => (
            <TaskFlowNode
              key={task.task_id}
              task={task}
              isFirst={index === 0}
              isLast={index === tasks.length - 1}
              allTasks={tasks}
            />
          ))}
        </div>
      </div>

      {/* Detailed Task List */}
      <div className="space-y-3">
        <h4 className="font-medium text-gray-900 mb-3">{t('integrations.task_details', 'Task Details')}</h4>
        {tasks.map(task => (
          <AgentTaskCard
            key={task.task_id}
            task={task}
            expanded={expandedTasks.has(task.task_id)}
            onToggle={() => toggleTask(task.task_id)}
            allTasks={tasks}
          />
        ))}
      </div>
    </div>
  );
};

// Task Stat Component
interface TaskStatProps {
  label: string;
  count: number;
  color: 'gray' | 'blue' | 'green' | 'red' | 'yellow';
  icon?: React.ReactNode;
}

const TaskStat: React.FC<TaskStatProps> = ({ label, count, color, icon }) => {
  const colors = {
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    yellow: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <div className={`rounded-lg p-3 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        {icon && <div className="h-4 w-4">{icon}</div>}
        <p className="text-xs font-medium">{label}</p>
      </div>
      <p className="text-xl font-bold">{count}</p>
    </div>
  );
};

// Task Flow Node (simplified visual representation)
interface TaskFlowNodeProps {
  task: AgentTask;
  isFirst: boolean;
  isLast: boolean;
  allTasks: AgentTask[];
}

const TaskFlowNode: React.FC<TaskFlowNodeProps> = ({ task, isFirst, isLast, allTasks }) => {
  const statusColors = {
    pending: 'border-gray-300 bg-gray-50',
    running: 'border-blue-400 bg-blue-50 shadow-lg',
    completed: 'border-green-400 bg-green-50',
    failed: 'border-red-400 bg-red-50',
    retry: 'border-yellow-400 bg-yellow-50',
    cancelled: 'border-gray-400 bg-gray-100',
  };

  const statusIcons = {
    pending: <Clock className="h-4 w-4 text-gray-500" />,
    running: <Activity className="h-4 w-4 text-blue-600 animate-pulse" />,
    completed: <CheckCircle2 className="h-4 w-4 text-green-600" />,
    failed: <XCircle className="h-4 w-4 text-red-600" />,
    retry: <RefreshCw className="h-4 w-4 text-yellow-600" />,
    cancelled: <XCircle className="h-4 w-4 text-gray-500" />,
  };

  const agentIcons = {
    extractor_agent: <Database className="h-4 w-4" />,
    transformer_agent: <Zap className="h-4 w-4" />,
    validator_agent: <CheckCircle2 className="h-4 w-4" />,
    loader_agent: <Database className="h-4 w-4" />,
    coordinator_agent: <GitBranch className="h-4 w-4" />,
  };

  return (
    <div className="flex items-center gap-2">
      {!isFirst && <div className="h-px bg-gray-300 w-4" />}
      <div className={`border-2 rounded-lg px-3 py-2 flex items-center gap-2 flex-1 ${statusColors[task.status]}`}>
        {agentIcons[task.agent_type]}
        <span className="text-sm font-medium flex-1">{task.task_name}</span>
        {statusIcons[task.status]}
      </div>
      {!isLast && <ChevronRight className="h-4 w-4 text-gray-400" />}
    </div>
  );
};

// Agent Task Card (detailed view)
interface AgentTaskCardProps {
  task: AgentTask;
  expanded: boolean;
  onToggle: () => void;
  allTasks: AgentTask[];
}

const AgentTaskCard: React.FC<AgentTaskCardProps> = ({ task, expanded, onToggle, allTasks }) => {
  const { t } = useAppTranslation();
  const statusColors = {
    pending: 'border-l-gray-400 bg-gray-50',
    running: 'border-l-blue-500 bg-blue-50',
    completed: 'border-l-green-500 bg-green-50',
    failed: 'border-l-red-500 bg-red-50',
    retry: 'border-l-yellow-500 bg-yellow-50',
    cancelled: 'border-l-gray-500 bg-gray-100',
  };

  const statusIcons = {
    pending: <Clock className="h-5 w-5 text-gray-500" />,
    running: <Activity className="h-5 w-5 text-blue-600 animate-pulse" />,
    completed: <CheckCircle2 className="h-5 w-5 text-green-600" />,
    failed: <XCircle className="h-5 w-5 text-red-600" />,
    retry: <RefreshCw className="h-5 w-5 text-yellow-600 animate-spin" />,
    cancelled: <XCircle className="h-5 w-5 text-gray-500" />,
  };

  const agentTypeLabels = {
    extractor_agent: 'Extractor Agent',
    transformer_agent: 'Transformer Agent',
    validator_agent: 'Validator Agent',
    loader_agent: 'Loader Agent',
    coordinator_agent: 'Coordinator Agent',
  };

  // Find dependent tasks
  const dependencies = task.depends_on.map(depId =>
    allTasks.find(tsk => tsk.task_id === depId)
  ).filter(Boolean);

  return (
    <div className={`border-l-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm ${statusColors[task.status]}`}>
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Bot className="h-5 w-5 text-teal-600" />
          <div className="text-left">
            <p className="font-medium text-gray-900 dark:text-white">{task.task_name}</p>
            <p className="text-xs text-gray-600 dark:text-gray-300">{agentTypeLabels[task.agent_type]}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {task.retry_count > 0 && (
            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
              Retry {task.retry_count}
            </span>
          )}
          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
            Priority: {task.priority}
          </span>
          {statusIcons[task.status]}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-200 pt-3 space-y-3">
          {/* Task Type & Status */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600 font-medium mb-1">{t('integrations.task_type', 'Task Type')}</p>
              <p className="text-gray-900 dark:text-white">{task.task_type}</p>
            </div>
            <div>
              <p className="text-gray-600 font-medium mb-1">{t('integrations.status', 'Status')}</p>
              <p className="text-gray-900 capitalize">{task.status}</p>
            </div>
          </div>

          {/* Dependencies */}
          {dependencies.length > 0 && (
            <div>
              <p className="text-gray-600 font-medium mb-2 text-sm">{t('integrations.dependencies', 'Dependencies')}</p>
              <div className="space-y-1">
                {dependencies.map((dep) => dep && (
                  <div key={dep.task_id} className="flex items-center gap-2 text-sm">
                    {statusIcons[dep.status]}
                    <span className="text-gray-700 dark:text-gray-300">{dep.task_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Input Data */}
          {task.input_data && Object.keys(task.input_data).length > 0 && (
            <div>
              <p className="text-gray-600 font-medium mb-2 text-sm">{t('integrations.input_data', 'Input Data')}</p>
              <pre className="bg-gray-100 rounded p-2 text-xs overflow-x-auto">
                {JSON.stringify(task.input_data, null, 2)}
              </pre>
            </div>
          )}

          {/* Output Data */}
          {task.output_data && Object.keys(task.output_data).length > 0 && (
            <div>
              <p className="text-gray-600 font-medium mb-2 text-sm">{t('integrations.output_data', 'Output Data')}</p>
              <pre className="bg-gray-100 rounded p-2 text-xs overflow-x-auto">
                {JSON.stringify(task.output_data, null, 2)}
              </pre>
            </div>
          )}

          {/* Error Message */}
          {task.error_message && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-900 text-sm">{t('integrations.error', 'Error')}</p>
                  <p className="text-sm text-red-700 mt-1">{task.error_message}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
